import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Box, Grid } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import DateRangeSelector from "../../common/components/DateRangeSelector";
import PageHeader from "../../common/components/PageHeader";
import SortableCardWrapper from "../../common/components/SortableCardWrapper";
import { toast } from "../../common/components/Toast";
import { ANALYTICS_CARDS } from "../../common/constants/cardRegistry";
import { usePreviewMode } from "../../common/contexts/PreviewModeContext";
import { useSettings } from "../../common/contexts/SettingsContext";
import { saveMyPreferences } from "../../settings/api/displayTemplates";
import { getProjectAnalytics } from "../api/analytics";
import AssetComparisonPicker from "../components/AssetComparisonPicker";
import ComparisonTable from "../components/ComparisonTable";
import DetailedStatsCards from "../components/DetailedStatsCards";
import EarningsCard from "../components/EarningsCard";
import GeneralStatsCard from "../components/GeneralStatsCard";
import ImpressionRevenueOverTime from "../components/ImpressionRevenueOverTime";
import ImpressionsOverTime from "../components/ImpressionsOverTime";
import RentalsOverTime from "../components/RentalsOverTime";
import ROIOverTime from "../components/ROIOverTime";
import SalesOverTime from "../components/SalesOverTime";
import SalesStatsCard from "../components/SalesStatsCard";
import { PlatformSalesDetails } from "../components/PlatformSalesDetails";
import { SourceAnalytics } from "../components/SourceAnalytics";
import { TopPerfomingContentByImpressions } from "../components/TopPerfomingContentByImpressions";
import { TopPerfomingContentBySales } from "../components/TopPerfomingContentBySales";
import TotalEarningsOverTime from "../components/TotalEarningsOverTime";

// Comparison color palette
const COMPARISON_COLORS = [
  "#009efd",
  "#e74c3c",
  "#2ecc71",
  "#f39c12",
  "#9b59b6",
];

// Build registry lookup
const REGISTRY_MAP = Object.fromEntries(ANALYTICS_CARDS.map((c) => [c.key, c]));

// Cards that render inside the over-time charts Grid
const CHART_GRID_KEYS = new Set([
  "sales_over_time",
  "rentals_over_time",
  "impressions_over_time",
  "impression_revenue_over_time",
  "total_earnings_over_time",
  "roi_over_time",
]);

// Data field mapping for each chart card
const CHART_DATA_FIELDS = {
  sales_over_time: "sales",
  rentals_over_time: "rentals",
  impressions_over_time: "impressions",
  impression_revenue_over_time: "impression_revenue",
  total_earnings_over_time: null, // special: royalty_revenue + impression_revenue
  roi_over_time: "roi",
};

const STATS_GRID_KEYS = new Set(["sales_stats_card", "general_stats_card"]);

function Analytics() {
  const { t } = useTranslation("analytics");
  const [analytics, setAnalytics] = useState(null);
  const { analyticsCardConfig, updateCardChartType } = useSettings();
  const { isPreviewMode, updateCardConfig } = usePreviewMode();
  const location = useLocation();
  const navigate = useNavigate();

  // Read comparison asset_ids from URL
  const params = new URLSearchParams(location.search);
  const comparisonIds = params.getAll("asset_ids").map(Number).filter(Boolean);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const periodStart = searchParams.get("period_start");
    const periodEnd = searchParams.get("period_end");
    const assetIds = searchParams
      .getAll("asset_ids")
      .map(Number)
      .filter(Boolean);

    const period_range = {
      period_start: periodStart,
      period_end: periodEnd,
      asset_ids: assetIds.length > 0 ? assetIds : undefined,
    };

    const fetchAnalytics = async () => {
      try {
        const fetchedAnalytics = await getProjectAnalytics(period_range);
        setAnalytics(fetchedAnalytics);
      } catch (error) {
        toast.error(error.message || t("error"));
      }
    };

    fetchAnalytics();
  }, [location.search, t]);

  const handleComparisonChange = useCallback(
    (newIds) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("asset_ids");
      newIds.forEach((id) => searchParams.append("asset_ids", id));
      navigate({ search: searchParams.toString() }, { replace: true });
    },
    [location.search, navigate],
  );

  const handleChartTypeChange = useCallback(
    async (cardKey, newType) => {
      if (isPreviewMode) {
        updateCardConfig("analytics_config", cardKey, { chart_type: newType });
        return;
      }

      updateCardChartType("analytics_config", cardKey, newType);
      const updated = analyticsCardConfig.map((c) =>
        c.key === cardKey ? { ...c, chart_type: newType } : c,
      );
      try {
        await saveMyPreferences({ analytics_config: updated });
      } catch {
        // Silently fail
      }
    },
    [analyticsCardConfig, updateCardChartType, isPreviewMode, updateCardConfig],
  );

  // Build comparison datasets for chart cards
  const buildComparisonDatasets = (cardKey) => {
    if (!analytics?.comparison || comparisonIds.length === 0) return null;
    const field = CHART_DATA_FIELDS[cardKey];

    return comparisonIds
      .map((assetId, i) => {
        const assetData = analytics.comparison[assetId];
        if (!assetData?.time_stats) return null;
        const values =
          field === null
            ? assetData.time_stats.map(
                (s) => (s.royalty_revenue || 0) + (s.impression_revenue || 0),
              )
            : assetData.time_stats.map((s) => s[field] || 0);

        // Find asset title from the comparison data
        const title = assetData.sales?.[0]?.asset_title || `Asset ${assetId}`;

        return {
          label: title,
          data: values,
          color: COMPARISON_COLORS[i % COMPARISON_COLORS.length],
        };
      })
      .filter(Boolean);
  };

  // Map card keys to render functions
  const ANALYTICS_CARD_COMPONENTS = {
    sales_over_time: ({ analytics: a, cardConfig }) => (
      <SalesOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    rentals_over_time: ({ analytics: a, cardConfig }) => (
      <RentalsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    impressions_over_time: ({ analytics: a, cardConfig }) => (
      <ImpressionsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    impression_revenue_over_time: ({ analytics: a, cardConfig }) => (
      <ImpressionRevenueOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    total_earnings_over_time: ({ analytics: a, cardConfig }) => (
      <TotalEarningsOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    roi_over_time: ({ analytics: a, cardConfig }) => (
      <ROIOverTime
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        comparisonDatasets={buildComparisonDatasets(cardConfig.key)}
        cardKey={cardConfig.key}
        configKey="analytics_config"
      />
    ),
    sales_stats_card: ({ analytics: a }) => <SalesStatsCard analytics={a} />,
    general_stats_card: ({ analytics: a }) => (
      <GeneralStatsCard analytics={a} showAssetCount={true} />
    ),
    earnings_card: ({ analytics: a }) => <EarningsCard analytics={a} />,
    detailed_stats_cards: ({ analytics: a }) => (
      <DetailedStatsCards analytics={a} />
    ),
    source_analytics: ({ analytics: a }) => <SourceAnalytics analytics={a} />,
    platform_sales_details: ({ analytics: a }) => (
      <PlatformSalesDetails analytics={a} />
    ),
    top_by_impressions: () => <TopPerfomingContentByImpressions />,
    top_by_sales: () => <TopPerfomingContentBySales />,
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !isPreviewMode) return;

      const oldIndex = analyticsCardConfig.findIndex(
        (c) => c.key === active.id,
      );
      const newIndex = analyticsCardConfig.findIndex((c) => c.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...analyticsCardConfig];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      reordered.forEach((card, i) => {
        updateCardConfig("analytics_config", card.key, { order: i + 1 });
      });
    },
    [analyticsCardConfig, isPreviewMode, updateCardConfig],
  );

  const visibleCards = analyticsCardConfig.filter((c) => c.visible);
  const chartCards = visibleCards.filter((c) => CHART_GRID_KEYS.has(c.key));
  const statsCards = visibleCards.filter((c) => STATS_GRID_KEYS.has(c.key));
  const otherCards = visibleCards.filter(
    (c) => !CHART_GRID_KEYS.has(c.key) && !STATS_GRID_KEYS.has(c.key),
  );

  const renderCard = (cardConfig) => {
    const Component = ANALYTICS_CARD_COMPONENTS[cardConfig.key];
    if (!Component) return null;
    const rendered = (
      <Component
        key={cardConfig.key}
        analytics={analytics}
        cardConfig={cardConfig}
      />
    );
    if (isPreviewMode) {
      return (
        <SortableCardWrapper key={cardConfig.key} id={cardConfig.key}>
          {rendered}
        </SortableCardWrapper>
      );
    }
    return rendered;
  };

  const cardContent = (
    <>
      {analytics?.comparison && comparisonIds.length > 0 && (
        <ComparisonTable
          comparison={analytics.comparison}
          colors={COMPARISON_COLORS}
        />
      )}

      {chartCards.length > 0 && (
        <Grid container columnSpacing={3}>
          {chartCards.map((cardConfig) => renderCard(cardConfig))}
        </Grid>
      )}

      {statsCards.length > 0 && (
        <Grid container spacing={3}>
          {statsCards.map((cardConfig) => renderCard(cardConfig))}
        </Grid>
      )}

      {otherCards.map((cardConfig) => renderCard(cardConfig))}
    </>
  );

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <AssetComparisonPicker
              selectedIds={comparisonIds}
              onChange={handleComparisonChange}
            />
            <DateRangeSelector />
          </Box>
        }
      />

      {!analytics ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <Spinner animation="border" />
        </Box>
      ) : isPreviewMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleCards.map((c) => c.key)}
            strategy={rectSortingStrategy}
          >
            {cardContent}
          </SortableContext>
        </DndContext>
      ) : (
        cardContent
      )}
    </>
  );
}

export default Analytics;
